import { createFileRoute, notFound } from "@tanstack/react-router";
import { getEsCalculatorBySlug, getEnCalculatorSlugForEsSlug, getEsCalculatorSlugForEnSlug } from "@/lib/es-calculators-catalog";
import { getDeCalculatorSlugForEnSlug } from "@/lib/de-calculators-catalog";
import { getFrCalculatorSlugForEnSlug } from "@/lib/fr-calculators-catalog";
import { getPtBrCalculatorSlugForEnSlug } from "@/lib/pt-br-calculators-catalog";
import { CALCULATOR_CONTENT } from "@/lib/calculator-content";
import { CalculatorShell } from "@/components/calculators/CalculatorShell";
import { CalculatorRenderer } from "@/components/calculators/CalculatorRenderer";

export const Route = createFileRoute("/es/calculators/$slug")({
  loader: ({ params }) => {
    const esCalc = getEsCalculatorBySlug(params.slug);
    if (!esCalc) throw notFound();
    const enSlug = getEnCalculatorSlugForEsSlug(esCalc.slug);
    const content = CALCULATOR_CONTENT[enSlug];
    if (!content) throw notFound();
    return { esCalc, enSlug, content };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { esCalc, enSlug } = loaderData;
    const currentCanonical = `https://picamini.online/es/calculators/${esCalc.slug}`;
    const enUrl = `https://picamini.online/calculators/${enSlug}`;
    const deUrl = `https://picamini.online/de/calculators/${getDeCalculatorSlugForEnSlug(enSlug)}`;
    const frUrl = `https://picamini.online/fr/calculators/${getFrCalculatorSlugForEnSlug(enSlug)}`;
    const ptUrl = `https://picamini.online/pt-br/calculators/${getPtBrCalculatorSlugForEnSlug(enSlug)}`;

    return {
      meta: [
        { title: `${esCalc.name} | Picamini.online` },
        { name: "description", content: esCalc.description },
        { name: "keywords", content: esCalc.keywords.join(", ") },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: esCalc.name },
        { property: "og:description", content: esCalc.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: currentCanonical },
        { property: "og:locale", content: "es_ES" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${esCalc.name} - Calculadora Gratuita en Picamini.online` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: esCalc.name },
        { name: "twitter:description", content: esCalc.description },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "noindex, follow" },
      ],
      links: [
        { rel: "canonical", href: currentCanonical },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "es", href: currentCanonical },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "fr", href: frUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: ptUrl },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": esCalc.name,
            "url": currentCanonical,
            "description": esCalc.description,
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "All",
            "inLanguage": "es",
            "browserRequirements": "Requiere JavaScript. Requiere HTML5.",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://picamini.online/es/" },
              { "@type": "ListItem", "position": 2, "name": "Calculadoras", "item": "https://picamini.online/es/calculators" },
              { "@type": "ListItem", "position": 3, "name": esCalc.name, "item": currentCanonical },
            ],
          }),
        },
      ],
    };
  },
  component: SingleEsCalculatorPage,
});

function SingleEsCalculatorPage() {
  const { esCalc, enSlug, content } = Route.useLoaderData();

  const calcMetaAdapter = {
    slug: esCalc.slug,
    name: esCalc.name,
    tagline: esCalc.tagline,
    description: esCalc.description,
    category: esCalc.category as any,
    icon: esCalc.icon,
    featured: esCalc.featured,
    keywords: esCalc.keywords,
  };

  return (
    <CalculatorShell calc={calcMetaAdapter} content={content} lang="es">
      <CalculatorRenderer slug={enSlug} />
    </CalculatorShell>
  );
}
